import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Minus, Plus, Trash2, ArrowRight, ArrowLeft, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useShop } from '../context/ShopContext';
import { useFavorites } from '../hooks/useFavorites';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL;

export default function CartPage() {
    const navigate = useNavigate();
    const { cart, updateCartItem, removeFromCart, itemCount } = useCart();
    const { themeData } = useTheme();
    const { merchantId } = useShop();
    const { isFavorite, toggleFavorite } = useFavorites(merchantId);
    const emptyCartTitle = themeData?.customization?.empty_messages?.empty_cart || 'Το καλάθι σας είναι άδειο.';

    const handleQuantityChange = (productId, delta) => {
        const item = cart.items.find(i => i.product_id === productId);
        if (item) {
            const newQuantity = item.quantity + delta;
            if (newQuantity > 0) {
                updateCartItem(productId, newQuantity);
            } else {
                removeFromCart(productId);
            }
        }
    };

    if (cart.items.length === 0) {
        return (
            <div className="min-h-screen bg-background">
                <Header />

                <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16 text-center">
                    <ShoppingBag className="w-16 h-16 sm:w-20 sm:h-20 text-muted-foreground mx-auto mb-5 sm:mb-6" />
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold text-primary mb-3 sm:mb-4 px-1">
                        {emptyCartTitle}
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Ξεκινήστε τις αγορές σας για να προσθέσετε προϊόντα
                    </p>
                    <Link to="/">
                        <Button className="rounded-full" data-testid="browse-products-btn">
                            Περιήγηση Προϊόντων
                        </Button>
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
            <Header />

            <main className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 sm:mb-6 touch-manipulation"
                >
                    <ArrowLeft className="w-4 h-4 shrink-0" />
                    Συνέχεια Αγορών
                </Link>

                <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-primary mb-5 sm:mb-8">
                    Καλάθι Αγορών
                </h1>

                <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
                    <div className="lg:col-span-2 space-y-3 sm:space-y-4">
                        {cart.items.map((item, index) => (
                            <motion.div
                                key={item.product_id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-card overflow-hidden touch-manipulation"
                            >
                                <div className="flex gap-3 sm:gap-4">
                                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg sm:rounded-xl overflow-hidden flex-shrink-0 bg-muted/30">
                                        <img
                                            src={item.image_url?.startsWith('/api') ? `${API_URL.replace('/api', '')}${item.image_url}` : item.image_url}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0 flex flex-col gap-1.5 sm:gap-2">
                                        <h3 className="font-medium text-primary text-sm sm:text-base leading-snug line-clamp-3 sm:line-clamp-2">
                                            {item.name}
                                        </h3>
                                        <p className="text-muted-foreground text-xs sm:text-sm tabular-nums">
                                            {item.price.toFixed(2)}€ / τεμ.
                                        </p>

                                        <div className="mt-1 sm:mt-2 pt-2 sm:pt-0 border-t border-border/60 sm:border-0 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                                            <div className="flex items-center justify-between gap-2 sm:justify-start">
                                                <div className="flex items-center gap-1.5 sm:gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-9 w-9 sm:h-8 sm:w-8 rounded-full shrink-0"
                                                        onClick={() => handleQuantityChange(item.product_id, -1)}
                                                        data-testid={`decrease-qty-${item.product_id}`}
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </Button>
                                                    <span className="min-w-[2rem] text-center font-medium tabular-nums text-sm sm:text-base">
                                                        {item.quantity}
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-9 w-9 sm:h-8 sm:w-8 rounded-full shrink-0"
                                                        onClick={() => handleQuantityChange(item.product_id, 1)}
                                                        data-testid={`increase-qty-${item.product_id}`}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                <span className="font-semibold tabular-nums text-sm sm:text-base sm:hidden">
                                                    {item.subtotal.toFixed(2)}€
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-end gap-2 sm:gap-3 w-full sm:w-auto">
                                                <span className="font-semibold tabular-nums text-sm sm:text-base hidden sm:inline">
                                                    {item.subtotal.toFixed(2)}€
                                                </span>
                                                <div className="flex items-center gap-0 sm:gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className={`h-9 w-9 sm:h-8 sm:w-8 rounded-full shrink-0 ${isFavorite(item.product_id) ? 'text-red-600' : 'text-muted-foreground'}`}
                                                        aria-label={
                                                            isFavorite(item.product_id)
                                                                ? 'Αφαίρεση από αγαπημένα'
                                                                : 'Προσθήκη σε αγαπημένα'
                                                        }
                                                        onClick={() => toggleFavorite(item.product_id)}
                                                        data-testid={`favorite-cart-${item.product_id}`}
                                                    >
                                                        <Heart className={`w-4 h-4 ${isFavorite(item.product_id) ? 'fill-current' : ''}`} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-9 w-9 sm:h-8 sm:w-8 rounded-full shrink-0 text-destructive hover:text-destructive"
                                                        onClick={() => removeFromCart(item.product_id)}
                                                        data-testid={`remove-item-${item.product_id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-card lg:sticky lg:top-24">
                            <h2 className="font-display text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Σύνοψη Παραγγελίας</h2>

                            <div className="space-y-2.5 sm:space-y-3 mb-4 sm:mb-6 text-sm">
                                <div className="flex justify-between gap-3 text-sm">
                                    <span className="text-muted-foreground leading-snug min-w-0">
                                        Υποσύνολο ({itemCount} {itemCount === 1 ? 'προϊόν' : 'προϊόντα'})
                                    </span>
                                    <span className="tabular-nums shrink-0">{cart.total.toFixed(2)}€</span>
                                </div>
                                <div className="flex justify-between gap-3 text-sm">
                                    <span className="text-muted-foreground">Αποστολή</span>
                                    <span className="text-green-600 shrink-0">Δωρεάν</span>
                                </div>
                            </div>

                            <div className="border-t pt-3 sm:pt-4 mb-4 sm:mb-6">
                                <div className="flex justify-between font-semibold text-base sm:text-lg gap-3">
                                    <span>Σύνολο</span>
                                    <span className="tabular-nums">{cart.total.toFixed(2)}€</span>
                                </div>
                            </div>

                            <Button
                                onClick={() => navigate('/checkout')}
                                className="w-full rounded-full h-11 sm:h-12 hidden lg:flex"
                                data-testid="proceed-to-checkout-btn"
                            >
                                Ολοκλήρωση Παραγγελίας
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>
            </main>

            <div
                className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 shadow-[0_-6px_24px_-4px_rgba(0,0,0,0.08)] px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
                role="region"
                aria-label="Γρήγορη ολοκλήρωση παραγγελίας"
            >
                <div className="max-w-4xl mx-auto flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground leading-none">Σύνολο</p>
                        <p className="text-lg font-semibold tabular-nums leading-tight mt-0.5">{cart.total.toFixed(2)}€</p>
                    </div>
                    <Button
                        onClick={() => navigate('/checkout')}
                        className="rounded-full h-11 px-5 shrink-0 min-w-[10rem] touch-manipulation"
                        data-testid="proceed-to-checkout-btn-mobile"
                    >
                        <span className="sm:hidden">Ταμείο</span>
                        <span className="hidden sm:inline">Ολοκλήρωση</span>
                        <ArrowRight className="w-4 h-4 ml-1.5 sm:ml-2" />
                    </Button>
                </div>
            </div>

            <Footer />
        </div>
    );
}
